import React from 'react'
import { createPortal } from 'react-dom'
import { CSSTransition } from 'react-transition-group'
import cn from 'classnames'
import { Empty, Pagination, Spin } from 'antd'
import { message } from 'antd'
import { $t } from '@/i18n/intl'
import dayjs from 'dayjs'
import styles from './index.module.scss'
import tabIconCompleted from '@/assets/images/tab-icon-completed.svg'
import tabIconGenerating from '@/assets/images/tab-icon-generating.svg'
import tabIconFailed from '@/assets/images/tab-icon-failed.svg'
import { getVideoListAPI, terminateVideoAPI } from '@/servers/agent-v2'
import type { VideoListItem } from '@/servers/agent-v2/types'
import { safeParseJson } from '@/utils'

const TRANSITION_TIMEOUT = 280
const DEFAULT_PAGE_SIZE = 8

export type VideoLibraryFilter = 'all' | 'completed' | 'generating' | 'failed' | 'queued'

export interface VideoLibraryLayerProps {
  visible: boolean
  onClose: () => void
  className?: string
}

const TAB_ICON = {
  completed: tabIconCompleted,
  generating: tabIconGenerating,
  failed: tabIconFailed
} as const

const FILTERS: { key: VideoLibraryFilter; label: string; iconSrc?: string }[] = [
  { key: 'all', label: '全部' },
  { key: 'completed', label: '已完成', iconSrc: TAB_ICON.completed },
  {
    key: 'generating',
    label: '生成中',
    iconSrc: TAB_ICON.generating
  },
  { key: 'queued', label: '排队中', iconSrc: TAB_ICON.generating },
  { key: 'failed', label: '失败', iconSrc: TAB_ICON.failed }
]

/** 筛选项对应接口 status：all 不传，generating 传 in_progress */
const FILTER_TO_API_STATUS: Record<VideoLibraryFilter, string | undefined> = {
  all: undefined,
  completed: 'completed',
  generating: 'in_progress',
  failed: 'failed',
  queued: 'queued'
}

/** 接口 status 映射为列表展示用状态 */
function mapApiStatusToDisplay(
  apiStatus: string
): 'completed' | 'generating' | 'queued' | 'failed' {
  if (apiStatus === 'completed') return 'completed'
  if (apiStatus === 'failed') return 'failed'
  if (apiStatus === 'queued') return 'queued'
  return 'generating'
}

const DETAIL_SLIDE_TIMEOUT = 300

interface VideoLibraryCardItem extends VideoListItem {
  creative_info_obj: {
    product_desc: string
    product_images: string[]
    final_script: string
  }
}

const parseCreativeInfo = (
  creativeInfo: string | null | undefined
): VideoLibraryCardItem['creative_info_obj'] => {
  const parsed = safeParseJson<Partial<VideoLibraryCardItem['creative_info_obj']>>(
    creativeInfo ?? ''
  )
  const normalized = parsed ?? {}

  return {
    product_desc: typeof normalized.product_desc === 'string' ? normalized.product_desc : '',
    product_images: Array.isArray(normalized.product_images) ? normalized.product_images : [],
    final_script: typeof normalized.final_script === 'string' ? normalized.final_script : ''
  }
}

const VideoLibraryLayer: React.FC<VideoLibraryLayerProps> = ({
  visible,
  onClose,
  className = ''
}) => {
  const [activeFilter, setActiveFilter] = React.useState<VideoLibraryFilter>('all')
  const [portalMounted, setPortalMounted] = React.useState(false)
  const [selectedCardId, setSelectedCardId] = React.useState<string | null>(null)
  const [displayDetailId, setDisplayDetailId] = React.useState<string | null>(null)
  const [listData, setListData] = React.useState<VideoLibraryCardItem[]>([])
  const [loading, setLoading] = React.useState(false)
  const [terminatingIds, setTerminatingIds] = React.useState<Set<string>>(new Set())
  const [page, setPage] = React.useState(1)
  const [pageSize, setPageSize] = React.useState(DEFAULT_PAGE_SIZE)
  const [total, setTotal] = React.useState(0)

  const getTableData = React.useCallback(
    async (params: { page: number; page_size: number; status?: string }) => {
      setLoading(true)
      try {
        const res = await getVideoListAPI({
          page: params.page,
          page_size: params.page_size,
          ...(params.status != null && { status: params.status })
        })
        if (res.code === 0 && res.data) {
          const data = res.data
          const list =
            data.list.map(item => ({
              ...item,
              creative_info_obj: parseCreativeInfo(item.creative_info)
            })) ?? []

          setListData(list)
          setTotal(data.total ?? 0)
        } else {
          res.message && message.error(res.message)
          setListData([])
          setTotal(0)
        }
      } catch (err) {
        message.error($t('demo.videoLibrary.loadFailed'))
        setListData([])
        setTotal(0)
      } finally {
        setLoading(false)
      }
    },
    []
  )

  React.useEffect(() => {
    if (visible) setPortalMounted(true)
  }, [visible])

  React.useEffect(() => {
    if (!visible) {
      setSelectedCardId(null)
      setDisplayDetailId(null)
    }
  }, [visible])

  React.useEffect(() => {
    if (selectedCardId) setDisplayDetailId(selectedCardId)
  }, [selectedCardId])

  React.useEffect(() => {
    if (!visible) return
    getTableData({
      page,
      page_size: pageSize,
      status: FILTER_TO_API_STATUS[activeFilter]
    })
  }, [visible, page, pageSize, activeFilter, getTableData])

  const handleExited = React.useCallback(() => {
    setPortalMounted(false)
    setSelectedCardId(null)
    setDisplayDetailId(null)
  }, [])

  const handleDetailExited = React.useCallback(() => {
    setDisplayDetailId(null)
  }, [])

  const onFilterChange = (key: VideoLibraryFilter) => {
    setActiveFilter(key)
    setPage(1)
  }

  const selectedCard = displayDetailId
    ? listData.find(item => item.video_id === displayDetailId)
    : null

  const handleDownloadVideo = async () => {
    if (!selectedCard?.video_url) {
      message.warning($t('demo.videoLibrary.noVideoToDownload'))
      return
    }
    const fileName = `video_${selectedCard.video_id || Date.now()}.mp4`
    try {
      const res = await fetch(selectedCard.video_url, { mode: 'cors' })
      if (!res.ok) throw new Error(res.statusText)
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      link.click()
      URL.revokeObjectURL(url)
      message.success($t('demo.videoLibrary.downloadSuccess'))
    } catch {
      window.open(selectedCard.video_url, '_blank', 'noopener,noreferrer')
      message.info($t('demo.videoLibrary.downloadOpenInNewTab'))
    }
  }

  const refreshCurrentPage = React.useCallback(() => {
    getTableData({
      page,
      page_size: pageSize,
      status: FILTER_TO_API_STATUS[activeFilter]
    })
  }, [activeFilter, getTableData, page, pageSize])

  const handleTerminateVideo = async (
    event: React.MouseEvent<HTMLButtonElement>,
    videoId: string
  ) => {
    event.stopPropagation()
    if (!videoId || terminatingIds.has(videoId)) return

    setTerminatingIds(prev => {
      const next = new Set(prev)
      next.add(videoId)
      return next
    })

    try {
      const res = await terminateVideoAPI({ video_id: videoId })
      if (res.code === 0) {
        message.success('已中止生成')
        refreshCurrentPage()
      } else {
        message.error(res.message || '中止生成失败')
      }
    } catch {
      message.error('中止生成失败')
    } finally {
      setTerminatingIds(prev => {
        const next = new Set(prev)
        next.delete(videoId)
        return next
      })
    }
  }

  if (!portalMounted || typeof document === 'undefined') return null

  const content = (
    <CSSTransition
      in={visible}
      appear
      timeout={TRANSITION_TIMEOUT}
      onExited={handleExited}
      classNames={{
        appear: styles.overlayEnter,
        appearActive: styles.overlayEnterActive,
        enter: styles.overlayEnter,
        enterActive: styles.overlayEnterActive,
        exit: styles.overlayExit,
        exitActive: styles.overlayExitActive
      }}
    >
      <div
        className={`${styles.overlay} ${className}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-label={$t('demo.videoLibrary.title')}
      >
        <div className={styles.mask} onClick={onClose} aria-hidden />
        <div className={styles.panel} onClick={e => e.stopPropagation()}>
          <div className={cn(styles.panelListWrap, selectedCardId && styles.panelListWrapBlurred)}>
            <div className={styles.header}>
              <div className={styles.headerText}>
                <h2 className={styles.title}>{$t('demo.videoLibrary.title')}</h2>
                <p className={styles.subtitle}>{$t('demo.videoLibrary.subtitle')}</p>
              </div>
              <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="关闭">
                ×
              </button>
            </div>

            <div className={styles.filters}>
              {FILTERS.map(({ key, label, iconSrc }) => (
                <div
                  key={key}
                  className={cn(styles.filterBtn, activeFilter === key && styles.filterBtnActive)}
                  onClick={() => onFilterChange(key)}
                >
                  {iconSrc && (
                    <span className={styles.filterIcon}>
                      <img src={iconSrc} alt="" width={20} height={20} />
                    </span>
                  )}
                  {label}
                </div>
              ))}
            </div>

            <Spin spinning={loading} className={styles.gridSpin}>
              {!loading && listData.length === 0 ? (
                <div className={styles.noData}>
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={$t('demo.videoLibrary.noData')}
                    className={styles.noDataEmpty}
                  />
                </div>
              ) : (
                <div className={styles.grid}>
                  {listData.map(item => {
                    const displayStatus = mapApiStatusToDisplay(item.status)
                    const isQueued = displayStatus === 'queued'
                    const isTerminating = terminatingIds.has(item.video_id)
                    const dateStr = item.created_at
                      ? dayjs(item.created_at).format('MM/DD HH:mm')
                      : '--'
                    const desc = item.creative_info_obj?.final_script ?? '--'
                    return (
                      <div
                        key={item.video_id}
                        className={styles.card}
                        onClick={() => setSelectedCardId(item.video_id)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            setSelectedCardId(item.video_id)
                          }
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <div className={styles.cardThumb}>
                          {displayStatus === 'completed' && (item.cover_url || item.video_url) && (
                            <>
                              {item.cover_url ? (
                                <img
                                  src={item.cover_url}
                                  alt=""
                                  className={styles.cardThumbCover}
                                />
                              ) : (
                                <video
                                  src={item.video_url}
                                  className={styles.cardThumbCover}
                                  muted
                                  preload="metadata"
                                  playsInline
                                />
                              )}
                            </>
                          )}
                          <div className={styles.cardPlay} />
                          <span
                            className={cn(
                              styles.cardTag,
                              displayStatus === 'completed' && styles.cardTagCompleted,
                              displayStatus === 'generating' && styles.cardTagNormal,
                              displayStatus === 'queued' && styles.cardTagNormal,
                              displayStatus === 'failed' && styles.cardTagFailed
                            )}
                          >
                            {displayStatus === 'completed' &&
                              $t('demo.videoLibrary.filterCompleted')}
                            {displayStatus === 'generating' &&
                              $t('demo.videoLibrary.filterGenerating')}
                            {displayStatus === 'queued' && '排队中'}
                            {displayStatus === 'failed' && $t('demo.videoLibrary.filterFailed')}
                          </span>
                        </div>
                        <p className={styles.cardDesc}>{desc}</p>
                        <div className={styles.cardMeta}>
                          <span className={styles.cardModel}>
                            {isQueued ? (
                              <button
                                type="button"
                                className={styles.cardTerminateBtn}
                                onClick={event => handleTerminateVideo(event, item.video_id)}
                                onKeyDown={event => event.stopPropagation()}
                                disabled={isTerminating}
                              >
                                {isTerminating ? '中止中' : '中止生成'}
                              </button>
                            ) : null}
                          </span>
                          <span className={styles.cardDate}>{dateStr}</span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </Spin>
            {total > 0 && (
              <div className={styles.paginationWrap}>
                <Pagination
                  current={page}
                  pageSize={pageSize}
                  total={total}
                  showSizeChanger
                  pageSizeOptions={[8, 12, 24, 48]}
                  showTotal={t => `共 ${t} 条`}
                  onChange={(p, size) => {
                    setPage(p)
                    setPageSize(size ?? pageSize)
                  }}
                  disabled={loading}
                />
              </div>
            )}
          </div>

          <CSSTransition
            in={!!selectedCardId}
            timeout={DETAIL_SLIDE_TIMEOUT}
            onExited={handleDetailExited}
            mountOnEnter
            unmountOnExit
            classNames={{
              enter: styles.detailEnter,
              enterActive: styles.detailEnterActive,
              exit: styles.detailExit,
              exitActive: styles.detailExitActive
            }}
          >
            <div className={styles.detailPanel}>
              {selectedCard && (
                <div className={styles.detailBody}>
                  <div className={styles.detailPlayerCol}>
                    <div className={styles.detailPlayer}>
                      <div className={styles.detailPlayerBg} />
                      {selectedCard.video_url ? (
                        <div className={styles.detailPlayerVideoWrap}>
                          <video
                            src={selectedCard.video_url}
                            className={styles.detailPlayerVideo}
                            controls
                            width="100%"
                            height="100%"
                            preload="metadata"
                            poster=""
                          />
                        </div>
                      ) : (
                        <div className={styles.detailPlayerPlay} />
                      )}
                    </div>
                  </div>
                  <div className={styles.detailAside}>
                    <div className={styles.detailInfoTop}>
                      <h3 className={styles.detailSectionTitle}>
                        {$t('demo.videoLibrary.detailVideoInfo')}
                      </h3>
                      <div className={styles.detailMeta}>
                        <div className={styles.detailMetaItem}>
                          <p className={styles.detailMetaLabel}>
                            {$t('demo.videoLibrary.detailStatus')}
                          </p>
                          <p className={styles.detailMetaValue}>
                            {selectedCard.status === 'completed' &&
                              $t('demo.videoLibrary.filterCompleted')}
                            {selectedCard.status === 'in_progress' &&
                              $t('demo.videoLibrary.filterGenerating')}
                            {selectedCard.status === 'queued' && '排队中'}
                            {selectedCard.status === 'failed' &&
                              $t('demo.videoLibrary.filterFailed')}
                            {!['completed', 'in_progress', 'queued', 'failed'].includes(
                              selectedCard.status
                            ) && selectedCard.status}
                          </p>
                        </div>
                        <div className={styles.detailMetaItem}>
                          <p className={styles.detailMetaLabel}>
                            {$t('demo.videoLibrary.detailDuration')}
                          </p>
                          <p className={styles.detailMetaValue}>{selectedCard.seconds}秒</p>
                        </div>
                        <div className={styles.detailMetaItem}>
                          <p className={styles.detailMetaLabel}>模型</p>
                          <p className={styles.detailMetaValue}>--</p>
                        </div>
                        <div className={styles.detailMetaItem}>
                          <p className={styles.detailMetaLabel}>
                            {$t('demo.videoLibrary.detailSize')}
                          </p>
                          <p className={styles.detailMetaValue}>{selectedCard.size}</p>
                        </div>
                        <div className={styles.detailMetaItem}>
                          <p className={styles.detailMetaLabel}>
                            {$t('demo.videoLibrary.detailCreatedAt')}
                          </p>
                          <p className={styles.detailMetaValue}>
                            {selectedCard.created_at
                              ? dayjs(selectedCard.created_at).format('YYYY/MM/DD HH:mm:ss')
                              : '--'}
                          </p>
                        </div>
                        {selectedCard.status === 'failed' && selectedCard.fail_reason && (
                          <div className={styles.detailMetaItem}>
                            <p className={styles.detailMetaLabel}>
                              {$t('demo.videoLibrary.detailFailReason')}
                            </p>
                            <p className={styles.detailMetaValue}>{selectedCard.fail_reason}</p>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className={styles.detailScriptWrap}>
                      <h3 className={styles.detailSectionTitle}>
                        {$t('demo.videoLibrary.detailScriptPrompt')}
                      </h3>
                      <div className={styles.detailDescScroll}>
                        <p className={styles.detailDesc}>
                          {selectedCard.creative_info_obj?.final_script || '--'}
                        </p>
                      </div>
                    </div>
                    <div className={styles.detailActions}>
                      <button
                        type="button"
                        className={styles.detailBtnPrimary}
                        onClick={handleDownloadVideo}
                        disabled={!selectedCard.video_url}
                      >
                        {$t('demo.videoLibrary.downloadVideo')}
                      </button>
                      <button
                        type="button"
                        className={styles.detailBtnSecondary}
                        onClick={() => setSelectedCardId(null)}
                      >
                        {$t('demo.videoLibrary.backToLibrary')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CSSTransition>
        </div>
      </div>
    </CSSTransition>
  )

  return createPortal(content, document.body)
}

export default VideoLibraryLayer
